import React from 'react';
import { Action } from 'kbar';
import {
  CLIENT_DETAILS_PAGE,
  FORMS_PAGE,
  SUBSCRIPTION_PAGE,
  CRM_PAGE,
  INVOICES_PAGE,
  KNOWLEDGE_BASE_PAGE,
} from '../constants/pathConsts';
import { RouteContext } from '../context/routeContext';
import {
  SidebarNavToEntityName,
  SidebarNavToEntityNameMapKey,
} from '../constants/shortcutConsts';
import { useKeyboardShortcutsV2 } from './useKeyboardShortcutsV2';
import { useSidebarItems } from '../components/Sidebar/useSidebarItems';
import history from '../history';

export const useCreateCommandAction = () => {
  const { pathname } = React.useContext(RouteContext);
  const isClientDetailsPage = pathname.includes(CLIENT_DETAILS_PAGE.path);
  const sidebarItems = useSidebarItems('main').flatMap(x => x.navItems);

  const handleNavigateToCreateEntityPage = (navId: string) => {
    switch (navId) {
      case 'sidebar-crm':
        history.push(`${CRM_PAGE.path}?openCreateModal=true`);
        break;
      case 'sidebar-forms':
        history.push(`${FORMS_PAGE.path}/edit`);
        break;
      case 'sidebar-invoices':
        history.push(`${INVOICES_PAGE.path}/edit`);
        break;
      case 'sidebar-subscriptions':
        history.push(`${SUBSCRIPTION_PAGE.path}/edit`);
        break;
      case 'sidebar-knowledgeBase':
        history.push(`${KNOWLEDGE_BASE_PAGE.path}/article/new`);
        break;
      default:
        break;
    }
  };

  /**
   * Convert each sidebar item to an create entity action.
   */
  const entityActions = React.useMemo(() => {
    const actions: Action[] = sidebarItems
      .filter(navItem => navItem.canAccess)
      .reduce((currentActions: Action[], navItem) => {
        // explicity define actions for payments sidebar nav sub-items
        if (navItem.htmlId === 'sidebar-payments') {
          currentActions.push({
            id: 'create-invoice-action',
            name: 'Create invoice',
            parent: 'create-entity-command',
            perform: () => handleNavigateToCreateEntityPage('sidebar-invoices'),
          });

          currentActions.push({
            id: 'create-subscription-action',
            name: 'Create subscription',
            parent: 'create-entity-command',
            perform: () =>
              handleNavigateToCreateEntityPage('sidebar-subscriptions'),
          });
        }

        if (
          SidebarNavToEntityName[navItem.htmlId as SidebarNavToEntityNameMapKey]
        ) {
          currentActions.push({
            id: `create-${navItem.htmlId}`,
            name: `Create ${SidebarNavToEntityName[
              navItem.htmlId as SidebarNavToEntityNameMapKey
            ].toLowerCase()}`,
            parent: 'create-entity-command',
            perform: () => {
              handleNavigateToCreateEntityPage(navItem.htmlId);
            },
          });
        }

        return currentActions;
      }, []);

    return actions;
  }, [sidebarItems]);

  useKeyboardShortcutsV2([
    {
      id: 'create-entity-command',
      name: 'Create...',
      // in client details page, create command should appear under 'General' section
      // otherwise, it should appear without a section
      section: isClientDetailsPage ? 'General' : undefined,
      shortcut: ['c'],
      keywords: 'create new invoice client',
    },
    ...entityActions,
  ]);
};
